import { Stack, Text, TextInput, Title, useMantineTheme } from '@mantine/core'
import { IconAlertTriangle, IconCircleCheck } from '@tabler/icons-react'
import { signMessage } from '@wagmi/core'
import React, { FC, useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { hashMessage, isAddress } from 'viem'

import RootButton from '@/components/Buttons/RootButton'
import ErrorMessage from '@/components/ErrorMessage'
import Loading from '@/components/Loading'
import { useAuth } from '@/plugins/auth'
import { getWagmiConfig } from '@/plugins/auth/config'
import ConnectMiddleware from '@/plugins/auth/RouteRedirectors/ConnectMiddleware'
import { BodyStack, StyledActionText, StyledInlineCode } from '@/styled/components'

import CopyButton from '../Buttons/CopyButton'
import CustomAlert from '../CustomAlert'
import publicViemClient, { VerifySignature } from './utils'

interface ErrorSigning {
  message: string
  details: string
}

const emptyErrorSigning: ErrorSigning = {
  message: '',
  details: '',
}

export const l1SignBotEndpoint = process.env.L1_SIGN_BOT_ENDPOINT || 'address-bot.lamina1.network'

const Component: FC = () => {
  const theme = useMantineTheme()
  const { t } = useTranslation()
  const { status } = useAuth()

  // Extract query parameters
  const [searchParams] = useSearchParams()
  const queryMessage = searchParams.get('message') || ''
  const queryEndpoint = searchParams.get('endpoint') || ''

  const [inputMessage, setInputMessage] = useState(queryMessage)
  const [signature, setSignature] = useState('')
  const [isValid, setIsValid] = useState<boolean | undefined>(undefined)
  const [verify, setVerify] = useState<VerifySignature>({
    address: '',
    message: '',
    signHash: '',
  })
  const [isLoadingVerify, setIsLoadingVerify] = useState<boolean>(false)
  const [confirmed, setConfirmed] = useState(false)
  const [errorSigning, setErrorSigning] = useState<ErrorSigning>(emptyErrorSigning)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  // extract platform name from message, first key is the platform name with "_id"
  const platform = useMemo(() => {
    try {
      const jsonObject = JSON.parse(inputMessage)
      const firstKey = Object.keys(jsonObject)[0]
      // return the platform name with first letter capitalized
      return firstKey.split('_')[0].charAt(0).toUpperCase() + firstKey.split('_')[0].slice(1)
    } catch (e) {
      // If inputMessage is not a valid JSON, return an empty string
      return ''
    }
  }, [inputMessage])

  const isDisabledSign = !inputMessage || isLoading || status !== 'connected'

  // Check if the endpoint is using HTTPS; if not, return a warning message.
  const warningHttps = useMemo(() => {
    if (!queryEndpoint) return false
    if (!queryEndpoint.startsWith('https://')) {
      return t('modals.signature.warningHttps', 'Endpoint must use TLS (https).')
    }
    return false
  }, [queryEndpoint])

  // Check if the endpoint matches the known Lamina1 Discord endpoint; if so, return true
  const warningL1Endpoint = useMemo(() => {
    if (!queryEndpoint) return ''
    if (queryEndpoint.replace('https://', '').startsWith(l1SignBotEndpoint)) {
      return true
    }
    return false
  }, [queryEndpoint])

  const endpointInfoMessage = useMemo(
    () => (
      <>
        {warningHttps ? (
          <CustomAlert
            icon={<IconAlertTriangle size={16} />}
            color={theme.colors.notification[0]}
            title={t('modals.signature.endpointInvalidTitle', 'Invalid Endpoint')}
          >
            <Link to={decodeURIComponent(queryEndpoint)} target="_blank">
              {decodeURIComponent(queryEndpoint)}
            </Link>
            <br />
            {t(
              'modals.signature.endpointInvalidMessage',
              'The endpoint you’re directing this signature to must use TLS (https://) and be in the right format. Please adjust or alert the endpoint developer, then try again.'
            )}
          </CustomAlert>
        ) : warningL1Endpoint ? (
          <CustomAlert
            icon={<IconCircleCheck size={16} />}
            color={theme.colors.l1Primary[0]}
            title={t('modals.signature.endpointConfirmedTitle', 'Endpoint confirmed')}
          >
            <Link to={decodeURIComponent(queryEndpoint)} target="_blank">
              {decodeURIComponent(queryEndpoint)}
            </Link>
          </CustomAlert>
        ) : (
          <CustomAlert
            icon={<IconAlertTriangle size={16} />}
            color={theme.colors.barChartYellow[0]}
            title={t('modals.signature.externalEndpointTitle', 'Connect to External Application?')}
          >
            <Link to={decodeURIComponent(queryEndpoint)} target="_blank">
              {decodeURIComponent(queryEndpoint)}
            </Link>
            <br />
            {t(
              'modals.signature.externalEndpointMessage',
              'The LAMINA1 Hub does not recognize the recipient application’s address. Make sure you trust the URL above before confirming your signature.'
            )}
          </CustomAlert>
        )}
      </>
    ),
    [queryEndpoint, warningHttps, warningL1Endpoint]
  )

  const postConfirmationL1EndpointMessage =
    platform === 'Discord' ? (
      <Trans i18nKey="modals.signature.endpointConfirmedMessage">
        {'Return to the '}
        <StyledInlineCode>/registration</StyledInlineCode>
        {' bot on Discord and hit  '}
        <StyledActionText>Refresh</StyledActionText>
        {
          ' to make sure your account is connected. You can disconnect your accounts or connect new accounts via the '
        }
        <StyledInlineCode>/registration</StyledInlineCode>
        {' bot anytime.'}
      </Trans>
    ) : (
      t(
        'modals.signature.endpointConfirmedMessage2',
        'Return to the {{platform}} bot to make sure your account is successfully connected.',
        { platform }
      )
    )

  const msgHash = useMemo(() => {
    if (!inputMessage) return ''
    return hashMessage(inputMessage)
  }, [inputMessage])

  const handleSign = useCallback(async () => {
    setErrorSigning(emptyErrorSigning)
    if (!inputMessage) return
    try {
      setIsLoading(true)
      const sig = await signMessage(getWagmiConfig(), { message: inputMessage })
      setSignature(sig)
    } catch (error: any) {
      setErrorSigning({
        message: t('modals.signature.errorSigning', 'Error requesting message signature'),
        details: error.message,
      })
      setIsLoading(false)
    }
    setIsLoading(false)
  }, [inputMessage])

  const handleVerify = useCallback(async () => {
    try {
      setIsLoadingVerify(true)
      const valid = await publicViemClient.verifyMessage({
        address: verify.address as `0x${string}`,
        message: verify.message,
        signature: verify.signHash as `0x${string}`,
      })
      setIsValid(valid)
      setIsLoadingVerify(false)
    } catch (error: any) {
      setIsValid(false)
      console.error(error)
    }
  }, [verify])

  const handleConfirm = useCallback(async () => {
    setErrorSigning(emptyErrorSigning)
    try {
      setIsLoading(true)

      if (warningHttps) {
        setErrorSigning({
          message: t('modals.signature.endpointInvalid', 'Endpoint Invalid'),
          details: warningHttps,
        })
        setIsLoading(false)
        return
      }

      const response = await fetch(queryEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputMessage,
          msg_hash: msgHash,
          signature,
        }),
      })
      if (response.status === 200 || response.status === 201) {
        setConfirmed(true)
      } else {
        const body = await response.json()
        setErrorSigning({
          message: t('modals.signature.errorConfirming', 'Error confirming signature'),
          details: body.error,
        })
        setIsLoading(false)
        setConfirmed(false)
      }
    } catch (error: any) {
      console.log('error', error)
      setErrorSigning({
        message: t('modals.signature.errorConfirming', 'Error confirming signature'),
        details: error.message,
      })
      setIsLoading(false)
      setConfirmed(false)
    }
    setIsLoading(false)
  }, [signature, queryEndpoint, inputMessage, msgHash])

  const handleReset = useCallback(() => {
    setInputMessage('')
    setSignature('')
    setIsLoading(false)
    setErrorSigning(emptyErrorSigning)
    setConfirmed(false)
  }, [])

  const onChangeMessage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setInputMessage(event.currentTarget.value)
    setErrorSigning(emptyErrorSigning)
  }, [])

  const onChangeAddress = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setIsValid(undefined)
    const value = event.currentTarget.value as `0x${string}`
    setVerify(prev => ({
      ...prev,
      address: value,
    }))
  }, [])

  const onChangeSignHash = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setIsValid(undefined)
    const value = event.currentTarget.value as `0x${string}`
    setVerify(prev => ({
      ...prev,
      signHash: value,
    }))
  }, [])

  const onChangeVerifyMessage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setIsValid(undefined)
    const value = event.currentTarget.value as string
    setVerify(prev => ({
      ...prev,
      message: value,
    }))
  }, [])

  return (
    <ConnectMiddleware>
      <BodyStack gap="40px">
        <Stack>
          <Title order={2}>{t('components.wallet.sign', 'Sign Message')}</Title>
          <Stack gap="5px">
            {warningL1Endpoint && platform ? (
              <Text>
                {t(
                  'modals.signature.signingMessageForL1Platform',
                  'Signing this message will connect your account to {{platform}}, and allow the {{platform}} Bot to see into your on-chain assets for token-gated content, roles, and experiences on LAMINA1.',
                  { platform }
                )}
              </Text>
            ) : (
              <Text>
                {t(
                  'modals.signature.signingMessageGeneral',
                  'Securely sign messages with your LAMINA1 account. Signing this message can be used to verify your identity, authorize transactions, or interact with decentralized applications.'
                )}
              </Text>
            )}
            {queryEndpoint && (
              <Text>
                {queryEndpoint &&
                  t(
                    'modals.signature.signingMessageGeneralEndpoint',
                    'The following message is set up to automatically forward your signature to an external application. Make sure you trust this application before you press Confirm.'
                  )}
              </Text>
            )}
          </Stack>
          <Stack>
            {!isLoading && !errorSigning?.message && signature ? (
              <>
                <Stack style={{ flexDirection: 'row' }} justify="space-between" align="baseline">
                  <Title order={5}>{t('modals.signature.resultTitle', 'Signature')}</Title>
                  <CopyButton value={signature} transparent />
                </Stack>
                <Text
                  style={{
                    wordWrap: 'break-word',
                    width: '100%',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    whiteSpace: 'wrap',
                    textAlign: 'left',
                    fontSize: '16px',
                    fontFamily: 'monospace',
                  }}
                >
                  {signature}
                </Text>
              </>
            ) : (
              <TextInput
                style={{ textAlign: 'left', color: theme.colors.paragraph[0] }}
                label={t('modals.signature.message', 'Message to sign')}
                value={inputMessage}
                onChange={onChangeMessage}
                placeholder={t('modals.signature.messagePlaceholder', 'Enter your message here')}
              />
            )}
            {errorSigning?.message !== '' && (
              <ErrorMessage
                fullWidth
                openDetails
                message={errorSigning.message}
                details={
                  errorSigning.details.includes('User rejected') ? (
                    <>
                      {errorSigning.details} + <br />
                      <Text>
                        Press <StyledActionText>Sign</StyledActionText> again and make sure you
                        confirm the transaction in your Metamask wallet. If you reject the
                        transaction, your signature will not go through.
                      </Text>
                    </>
                  ) : (
                    errorSigning.details
                  )
                }
              />
            )}
            {isLoading ? (
              <Loading />
            ) : (
              <>
                {!confirmed ? (
                  <>
                    {queryEndpoint && endpointInfoMessage}
                    <Stack
                      style={{ flexDirection: 'row' }}
                      justify="space-between"
                      align="baseline"
                    >
                      {signature && (
                        <RootButton expand secondary onClick={handleReset}>
                          {t('buttons.reset', 'Reset')}
                        </RootButton>
                      )}
                      {!signature && (
                        <RootButton expand onClick={handleSign} disabled={isDisabledSign}>
                          {t('buttons.sign', 'Sign')}
                        </RootButton>
                      )}
                      {queryEndpoint && (
                        <RootButton
                          expand
                          onClick={handleConfirm}
                          disabled={!!warningHttps || !signature}
                        >
                          {t('buttons.confirm', 'Confirm')}
                        </RootButton>
                      )}
                    </Stack>
                  </>
                ) : (
                  <Stack style={{ display: 'flex', alignItems: 'center' }}>
                    {warningL1Endpoint && (
                      <CustomAlert
                        icon={<IconCircleCheck size={16} />}
                        color={theme.colors.l1Primary[0]}
                        title={t(
                          'modals.signature.confirmed',
                          'Message signed and signature successfully sent!'
                        )}
                      >
                        {postConfirmationL1EndpointMessage}
                      </CustomAlert>
                    )}
                  </Stack>
                )}
              </>
            )}
          </Stack>
        </Stack>
        <Stack>
          <Title order={2}>{t('components.wallet.verify', 'Verify Message')}</Title>
          <Stack gap="5px">
            <Text>
              {t(
                'modals.signature.verifyMessage',
                'Verify the signature of a message to ensure it was signed by the owner of the address.'
              )}
            </Text>
            <Stack gap="15px">
              <TextInput
                style={{ textAlign: 'left', color: theme.colors.paragraph[0] }}
                label={t('modals.signature.addr', 'Address to verify')}
                value={verify.address}
                onChange={onChangeAddress}
                placeholder={t('modals.signature.verPlaceholder', 'Enter the address here')}
                error={verify.address && !isAddress(verify.address) ? 'Invalid address' : ''}
              />
              <TextInput
                style={{ textAlign: 'left', color: theme.colors.paragraph[0] }}
                label={t('modals.signature.msg', 'Message to verify')}
                value={verify.message}
                onChange={onChangeVerifyMessage}
                placeholder={t('modals.signature.msgPlaceholder', 'Enter the message here')}
              />
              <TextInput
                style={{ textAlign: 'left', color: theme.colors.paragraph[0] }}
                label={t('modals.signature.sig', 'Signature to verify')}
                value={verify.signHash}
                onChange={onChangeSignHash}
                placeholder={t('modals.signature.sigPlaceholder', 'Enter the signature hash here')}
                error={
                  verify.signHash && !verify.signHash.startsWith('0x') ? 'Invalid signature' : ''
                }
              />
              <Stack style={{ flexDirection: 'row' }}>
                <RootButton
                  expand
                  onClick={handleVerify}
                  disabled={
                    !verify.address ||
                    !verify.message ||
                    !verify.signHash ||
                    !isAddress(verify.address) ||
                    !verify.signHash.startsWith('0x')
                  }
                >
                  {t('buttons.verify', 'Verify')}
                </RootButton>
              </Stack>
              {!isLoadingVerify ? (
                <>
                  {isValid !== undefined && (
                    <Stack style={{ display: 'flex', alignItems: 'center' }}>
                      {isValid ? (
                        <CustomAlert
                          icon={<IconCircleCheck size={16} />}
                          color={theme.colors.l1Primary[0]}
                          title={t(
                            'modals.verify.confirmed',
                            'Message signature successfully verified!'
                          )}
                        />
                      ) : (
                        <CustomAlert
                          icon={<IconAlertTriangle size={16} />}
                          color={theme.colors.notification[0]}
                          title={t('modals.verify.failed', 'Message signature verification failed')}
                        />
                      )}
                    </Stack>
                  )}
                </>
              ) : (
                <Loading />
              )}
            </Stack>
          </Stack>
        </Stack>
      </BodyStack>
    </ConnectMiddleware>
  )
}

Component.displayName = 'SignaturePage'

export { Component }
